import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle, Plus, Trash, Lightning,
} from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";

const STEPS = [
  { n: 1, code: "1", label: "Task Input", phase: "STRATEGY" },
  { n: 2, code: "2", label: "Urgent / Important", phase: "STRATEGY" },
  { n: 3, code: "3", label: "Action Routing", phase: "STRATEGY" },
  { n: 4, code: "4", label: "Delegation Output", phase: "STRATEGY" },
];
const LAST_STEP = STEPS.length;
const uid = (p) => p + "_" + Math.random().toString(36).slice(2, 10);

export function quadrant(u, i) {
  if (u && i) return { key: "Q1", action: "Do", label: "Do First", chip: "bg-red-50 text-red-700 border border-red-200", bar: "bg-red-500" };
  if (!u && i) return { key: "Q2", action: "Schedule", label: "Schedule", chip: "bg-blue-50 text-blue-700 border border-blue-200", bar: "bg-blue-500" };
  if (u && !i) return { key: "Q3", action: "Delegate", label: "Delegate", chip: "bg-amber-50 text-amber-700 border border-amber-200", bar: "bg-amber-500" };
  return { key: "Q4", action: "Eliminate", label: "Eliminate", chip: "bg-slate-100 text-slate-600 border border-slate-200", bar: "bg-slate-400" };
}

const ACTIONS = ["Do", "Schedule", "Delegate", "Eliminate"];
const labelCls = "block text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1.5";
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

const Toggle = ({ on, onClick, testid, children }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition-all ${on ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
  >
    {children}
  </button>
);

export default function EisenhowerMatrix() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");
  const [tasks, setTasks] = useState([]);
  const [coPilotOpen, setCoPilotOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/documents/${id}`);
        setTitle(res.data.title || "");
        const p = res.data.payload || {};
        setContext(p.context || "");
        setTasks((p.tasks || []).map((t) => ({ urgent: false, important: false, action: "", delegateTo: "", notes: "", ...t })));
      } catch {
        toast.error("Document not found");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try {
      await api.put(`/documents/${id}`, { title: title || "Untitled Priority Matrix", payload: { context, tasks }, ...extra });
      return true;
    } catch {
      toast.error("Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  }, [id, title, context, tasks]);

  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST_STEP, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => {
    if (await persist({ status: "Completed" })) {
      toast.success("Priority matrix finalised");
      navigate(`/matrix/${id}/view`);
    }
  };

  // task helpers
  const addTask = () => setTasks((t) => [...t, { id: uid("tsk"), text: "", urgent: false, important: false, action: "", delegateTo: "", notes: "" }]);
  const setTask = (idx, patch) => setTasks((t) => t.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const removeTask = (idx) => setTasks((t) => t.filter((_, i) => i !== idx));

  // when leaving step 2, auto-suggest the routing action from the quadrant if unset
  const autoRoute = () => setTasks((t) => t.map((x) => ({ ...x, action: x.action || quadrant(x.urgent, x.important).action })));

  const applySuggestion = (text) => {
    if (step === 1) {
      setTasks((t) => [...t, { id: uid("tsk"), text, urgent: false, important: false, action: "", delegateTo: "", notes: "" }]);
      toast.success("Added as a task");
    } else {
      navigator.clipboard?.writeText(text);
      toast.info("Suggestion copied to clipboard");
    }
  };

  if (loading)
    return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  const validTasks = tasks.filter((t) => t.text && t.text.trim());
  const delegated = tasks.filter((t) => (t.action || quadrant(t.urgent, t.important).action) === "Delegate" && t.text?.trim());

  return (
    <Layout>
      <div className="max-w-5xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3">
          <ArrowLeft size={16} /> Workspace
        </button>
        <input
          data-testid="matrix-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Priority matrix title…"
          className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1"
        />

        {/* Step rail */}
        <div className="mt-5 flex items-center gap-2" data-testid="matrix-stepper">
          {STEPS.map((s) => (
            <button
              key={s.n}
              data-testid={`step-${s.n}`}
              onClick={() => setStep(s.n)}
              className={`flex-1 rounded-sm border px-3 py-2.5 text-left transition-all ${
                step === s.n ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <span className={`font-mono text-xs font-bold ${step === s.n ? "text-indigo-700" : "text-slate-400"}`}>STEP {s.n}</span>
              <p className={`text-sm font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          {/* STEP 1 — Task input */}
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Capture your commitments</h2>
              <div>
                <label className={labelCls}>Context & Objectives</label>
                <textarea data-testid="field-context" rows={3} className={taCls} value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="What goals or period are you prioritising against? (optional)" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={labelCls}>Tasks & Commitments</label>
                  <button data-testid="add-task-button" onClick={addTask} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                    <Plus size={16} /> Add task
                  </button>
                </div>
                {tasks.length === 0 && (
                  <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">No tasks yet. Add the work competing for your attention.</p>
                )}
                <div className="space-y-2">
                  {tasks.map((t, i) => (
                    <div key={t.id} className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-400 w-5">{i + 1}.</span>
                      <input data-testid={`task-text-${i}`} value={t.text} onChange={(e) => setTask(i, { text: e.target.value })}
                        placeholder="Describe the task or commitment…" className={inputCls + " flex-1"} />
                      <button data-testid={`remove-task-${i}`} onClick={() => removeTask(i)} className="p-2 text-slate-400 hover:text-red-600"><Trash size={16} /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — Urgent / Important placement */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Classify each task</h2>
              <p className="text-sm text-slate-500">Mark whether each task is genuinely <strong>urgent</strong> (time-pressured) and/or <strong>important</strong> (advances your objectives).</p>
              {validTasks.length === 0 ? (
                <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add tasks in Step 1 first.</p>
              ) : (
                <div className="space-y-2">
                  {tasks.map((t, i) => {
                    if (!t.text?.trim()) return null;
                    const q = quadrant(t.urgent, t.important);
                    return (
                      <div key={t.id} data-testid={`classify-row-${i}`} className="flex flex-col sm:flex-row sm:items-center gap-3 border border-slate-200 rounded-sm p-3">
                        <span className="flex-1 text-sm text-slate-800">{t.text}</span>
                        <div className="flex items-center gap-2">
                          <Toggle testid={`urgent-${i}`} on={t.urgent} onClick={() => setTask(i, { urgent: !t.urgent })}>Urgent</Toggle>
                          <Toggle testid={`important-${i}`} on={t.important} onClick={() => setTask(i, { important: !t.important })}>Important</Toggle>
                          <span className={`px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wide ${q.chip}`}>{q.label}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — Action routing */}
          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Route each task to an action</h2>
              <p className="text-sm text-slate-500">The quadrant suggests a default — override it where your judgement differs.</p>
              {validTasks.length === 0 ? (
                <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add and classify tasks first.</p>
              ) : (
                <div className="space-y-2">
                  {tasks.map((t, i) => {
                    if (!t.text?.trim()) return null;
                    const q = quadrant(t.urgent, t.important);
                    const current = t.action || q.action;
                    return (
                      <div key={t.id} data-testid={`route-row-${i}`} className="flex flex-col lg:flex-row lg:items-center gap-3 border border-slate-200 rounded-sm p-3">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-slate-800">{t.text}</span>
                          <span className={`ml-2 px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wide ${q.chip}`}>{q.label}</span>
                        </div>
                        <div className="inline-flex rounded-sm bg-slate-100 p-1">
                          {ACTIONS.map((a) => (
                            <button key={a} data-testid={`action-${i}-${a.toLowerCase()}`} onClick={() => setTask(i, { action: a })}
                              className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${current === a ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                              {a}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 4 — Delegation output */}
          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Delegation plan</h2>
              <p className="text-sm text-slate-500">For every task routed to <strong>Delegate</strong>, name an accountable owner and the outcome they own.</p>
              {delegated.length === 0 ? (
                <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">No tasks are routed to Delegate. Adjust routing in Step 3 if needed.</p>
              ) : (
                <div className="space-y-3">
                  {tasks.map((t, i) => {
                    const action = t.action || quadrant(t.urgent, t.important).action;
                    if (action !== "Delegate" || !t.text?.trim()) return null;
                    return (
                      <div key={t.id} data-testid={`delegate-row-${i}`} className="border border-slate-200 rounded-sm p-4">
                        <p className="text-sm font-medium text-slate-900 mb-3">{t.text}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={labelCls}>Accountable Owner</label>
                            <input data-testid={`delegate-to-${i}`} value={t.delegateTo} onChange={(e) => setTask(i, { delegateTo: e.target.value })} placeholder="Who owns this outcome?" className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls}>Outcome & Authority</label>
                            <input data-testid={`delegate-notes-${i}`} value={t.notes} onChange={(e) => setTask(i, { notes: e.target.value })} placeholder="What success looks like + decision rights" className={inputCls} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nav controls */}
        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="matrix-back" onClick={back} disabled={step === 1}
            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="matrix-save-draft" onClick={saveDraft} disabled={saving}
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft
            </button>
            {step < LAST_STEP ? (
              <button data-testid="matrix-next" onClick={() => { if (step === 2) autoRoute(); next(); }}
                className="inline-flex items-center gap-2 rounded-sm bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">
                Next Step <ArrowRight size={16} />
              </button>
            ) : (
              <button data-testid="matrix-finalise" onClick={finalise} disabled={saving}
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700">
                <CheckCircle size={18} weight="fill" /> Finalise Matrix
              </button>
            )}
          </div>
        </div>
      </div>

      <CoPilotToggle open={coPilotOpen} onClick={() => setCoPilotOpen(true)} phase={STEPS[step - 1].phase} />
      <CoPilotPanel
        open={coPilotOpen}
        onClose={() => setCoPilotOpen(false)}
        docId={id}
        toolType="eisenhower"
        step={STEPS[step - 1]}
        phase={STEPS[step - 1].phase}
        onApplySuggestion={applySuggestion}
      />
    </Layout>
  );
}
